/* Route prefetch map.
 *
 * Maps top-level pathnames → the same dynamic import() that React.lazy() uses
 * in App.js. By calling these functions on link hover/focus, the browser
 * fetches the JS chunk before the user clicks — making navigation feel instant
 * even for heavy pages like /investment-board.
 *
 * Webpack/CRA caches resolved modules so calling the import multiple times is
 * cheap. Failures are swallowed silently (no UX regression if a prefetch fails).
 */

const ROUTE_PREFETCH = {
  '/': () => import('../pages/Home'),
  '/about': () => import('../pages/About'),
  '/membership': () => import('../pages/Membership'),
  '/events': () => import('../pages/Events'),
  '/blog': () => import('../pages/Blog'),
  '/business-directory': () => import('../pages/BusinessDirectory'),
  '/members': () => import('../pages/MemberDirectory'),
  '/investor-offers': () => import('../pages/InvestorOffers'),
  '/investment-board': () => import('../pages/InvestmentBoard'),
  '/city-chapters': () => import('../pages/Chapters'),
  '/member-benefits': () => import('../pages/MemberBenefits'),
  '/sponsorship': () => import('../pages/Sponsorship'),
  '/downloads': () => import('../pages/Downloads'),
  '/communities': () => import('../pages/Communities'),
  '/impact': () => import('../pages/Impact'),
  '/impact/new': () => import('../pages/ImpactNew'),
  '/impact/leaderboard': () => import('../pages/ImpactLeaderboard'),
  '/impact/map': () => import('../pages/ImpactMap'),
  '/faq': () => import('../pages/FAQ'),
  '/contact': () => import('../pages/Contact'),
  '/gallery': () => import('../pages/Gallery'),
  '/trust': () => import('../pages/TrustCenter'),
  '/privacy-policy': () => import('../pages/PrivacyPolicy'),
  '/dashboard': () => import('../pages/Dashboard'),
  '/login': () => import('../pages/Login'),
  '/register': () => import('../pages/Register'),
  '/provider-directory': () => import('../pages/ProviderDirectory'),
};

const prefetched = new Set();

/**
 * Fire the import for the given path. Idempotent — caches every successful
 * prefetch in a Set so we don't re-trigger the same import on repeated hovers.
 * Matches dynamic segments by trimming the path to its root (e.g. /impact/foo → /impact).
 */
export function prefetchRoute(path) {
  if (!path) return;

  let importer = ROUTE_PREFETCH[path];

  // Try the parent path (handles dynamic routes like /impact/:slug)
  if (!importer) {
    const parts = path.split('/').filter(Boolean);
    while (parts.length > 0) {
      const candidate = '/' + parts.join('/');
      if (ROUTE_PREFETCH[candidate]) {
        importer = ROUTE_PREFETCH[candidate];
        break;
      }
      parts.pop();
    }
  }
  if (!importer) return;

  const key = importer.toString();
  if (prefetched.has(key)) return;
  prefetched.add(key);

  // Fire-and-forget; swallow errors so prefetch never breaks anything
  try {
    const p = importer();
    if (p && typeof p.catch === 'function') p.catch(() => prefetched.delete(key));
  } catch {
    prefetched.delete(key);
  }
}

export default ROUTE_PREFETCH;
